// Survey Part 3: the One-Line (MOCKUP_IEA_ONE_LINE.html rev 3, approved by Robert 2026-10-21; AF_MAPPING_WORDS.md
// sections 5 and 15). Pure: the Drawing laid out and what the records say in, the one-line's symbols and places out.
//
// The one-line is drawn on the Drawing's own positions, so it reads the way the site was arranged. Each label is a
// bus, or the symbol of what it is (utility, transformer, generator, VFD, transfer switch). Each Fed From is a line
// from the feeder to the gear: the protective device on the position it leaves from, then the cable into the gear.
// Every item carries its SKM name, the tag given when it was mapped, then the Asset ID.

import { GRID, type Drawing } from "./survey-drawing";

export type OlKind = "utility" | "generator" | "transformer" | "vfd" | "ats" | "bus";

/** Levels sit this many times further apart than on the Drawing: room for the device and the cable on each line. */
export const OL_Y = 2;
/** A transfer switch's two input terminals sit this far either side of its center line. */
export const ATS_TERM = 10;
/** Half the height of a symbol that is not a bus bar. */
const SYM = 24;

export interface OlInfo {
  kind: (assetId: string) => OlKind;
  /** The SKM tag for an Item Key ("bus:01-ER1-SWBD-01"), or null until it is given. */
  tag: (key: string) => string | null;
  /** The node's one line of data: "Switchboard · 480 V", "1500 kVA · 5.75 %Z". */
  data: (assetId: string) => string;
  /** The device on a position: its words ("Thermal-Magnetic MCCB · 225 A") and whether it is a fuse. */
  device: (assetId: string, point: string) => { data: string; fuse: boolean };
  /** The ID of a position: its own Asset ID when it has a QR, else the dot ID (positionId). */
  positionId: (assetId: string, point: string) => string;
  /** The cable's words, or blank until the wire is filled. */
  wire: (linkId: string) => string;
}

export interface OlNode {
  assetId: string;
  kind: OlKind;
  name: string;
  tag: string | null;
  data: string;
  /** Bus bar ends, or the symbol's center line. */
  x0: number;
  x1: number;
  cx: number;
  /** The bus bar, or the symbol's middle. */
  y: number;
  /** Where lines come in and go out. */
  top: number;
  bottom: number;
}

export interface OlFeed {
  id: string;
  from: string;
  to: string;
  point: string;
  side: "" | "P" | "E";
  notVerified: boolean;
  /** The protective device on the position, when the line leaves from one. */
  pd: { id: string; tag: string | null; data: string; fuse: boolean; y: number } | null;
  cable: { tag: string | null; data: string; y: number };
  x1: number;
  y1: number;
  bus: number;
  x2: number;
  y2: number;
}

export interface OneLine {
  nodes: OlNode[];
  feeds: OlFeed[];
  box: { x0: number; y0: number; x1: number; y1: number };
}

/** The Item Key prefix for a node's own tag, by what it is (survey-tags.ts `typesForAsset`). */
const NODE_TYPE: Record<OlKind, string> = { utility: "utility", generator: "generator", transformer: "transformer", vfd: "vfd", ats: "transferswitch", bus: "bus" };

export function oneLine(d: Drawing, info: OlInfo): OneLine {
  const nodes: OlNode[] = d.labels.map((l) => {
    const kind = info.kind(l.assetId);
    const y = l.y * OL_Y + 40;
    const bar = kind === "bus";
    const half = kind === "utility" ? SYM + 22 : SYM;
    return {
      assetId: l.assetId,
      kind,
      name: l.name,
      tag: info.tag(`${NODE_TYPE[kind]}:${l.assetId}`),
      data: info.data(l.assetId),
      x0: l.x,
      x1: l.x + l.w,
      cx: l.x + l.w / 2,
      y,
      top: bar ? y : y - half,
      bottom: bar ? y : y + half,
    };
  });
  const at = new Map(nodes.map((n) => [n.assetId, n]));
  // A position's line leaves the bus where the Drawing put its tab.
  // The One-Line stays top to bottom (DRAWING_CONNECTORS_WORDS.md item 9): only tabs under a label place a line here.
  const under = d.tabs.filter((t) => t.side === "Bottom");
  const tabX = new Map(under.map((t) => [`${t.assetId}|${t.point}`, t.x + 17]));
  // Devices on neighboring positions step down one under the next, so their names never sit on each other.
  const step = new Map<string, number>();
  for (const t of under) step.set(`${t.assetId}|${t.point}`, under.filter((x) => x.assetId === t.assetId && x.x < t.x).length);
  const feeds: OlFeed[] = [];
  for (const l of d.lines) {
    const f = l.feed;
    const a = at.get(f.from);
    const b = at.get(f.to);
    if (!a || !b) continue;
    const x1 = f.point ? (tabX.get(`${f.from}|${f.point}`) ?? a.cx) : a.cx;
    const y1 = a.bottom;
    // A transfer switch has two inputs: Primary on its left terminal, Emergency on its right (ATS_TERM).
    const x2 = b.kind === "ats" && f.side ? b.cx + (f.side === "E" ? ATS_TERM : -ATS_TERM) : b.cx;
    const y2 = b.top;
    const pdId = f.point ? info.positionId(f.from, f.point) : "";
    const dev = f.point ? info.device(f.from, f.point) : null;
    const pdY = y1 + 14 + 44 * (step.get(`${f.from}|${f.point}`) ?? 0);
    const pd = f.point ? { id: pdId, tag: info.tag(`protectivedevice:${pdId}`), data: dev!.data, fuse: dev!.fuse, y: pdY } : null;
    const low = pd ? pd.y + 30 + 8 : y1 + 12;
    const bus = y2 - 3 * GRID > low ? y2 - 3 * GRID : Math.round((low + y2) / 2);
    feeds.push({
      id: f.id,
      from: f.from,
      to: f.to,
      point: f.point,
      side: f.side,
      notVerified: f.notVerified,
      pd,
      cable: { tag: info.tag(`cable:${f.id}`), data: info.wire(f.id), y: Math.round((bus + y2) / 2) },
      x1,
      y1,
      bus,
      x2,
      y2,
    });
  }
  const xs = nodes.flatMap((n) => [n.x0 - 20, n.x1 + 200]);
  const ys = nodes.flatMap((n) => [n.top - 40, n.bottom + 40]);
  const box = xs.length ? { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) } : { x0: 0, y0: 0, x1: 0, y1: 0 };
  return { nodes, feeds, box };
}

/** The model check (mockup rev 3): how many items KE gets, how many connections are Not Verified. */
export function modelCheck(ol: OneLine): { items: number; notVerified: number; untagged: number } {
  const items = ol.nodes.length + ol.feeds.length + ol.feeds.filter((f) => f.pd).length;
  const untagged = ol.nodes.filter((n) => !n.tag).length + ol.feeds.filter((f) => !f.cable.tag || (f.pd && !f.pd.tag)).length;
  return { items, notVerified: ol.feeds.filter((f) => f.notVerified).length, untagged };
}
